import {
  readItem,
  removeItem,
  writeItem,
  type StorageArea,
} from './storageArea';

export type { StorageArea };

export interface StorageOptions<T> {
  key: string;
  area: StorageArea;
  fallback: T;
  /**
   * Turns the JSON-parsed value into `T`. Throw (or return the fallback) to
   * reject untrusted input. Omitting it casts the stored value to `T`
   * unchecked — only safe when nothing else can write the key.
   */
  parse?: (parsed: unknown) => T;
}

export interface RawStorageOptions {
  key: string;
  area: StorageArea;
  raw: true;
  fallback?: string;
}

export interface TypedStorage<T> {
  get: () => T;
  set: (value: T | ((previous: T) => T)) => void;
  remove: () => void;
}

export function createStorage(options: RawStorageOptions): TypedStorage<string>;
export function createStorage<T>(options: StorageOptions<T>): TypedStorage<T>;
export function createStorage<T>(
  options: StorageOptions<T> | RawStorageOptions,
): TypedStorage<T> | TypedStorage<string> {
  const { key, area } = options;

  const remove = (): void => {
    removeItem(area, key);
  };

  if ('raw' in options) {
    const fallback = options.fallback ?? '';

    const getRaw = (): string => readItem(area, key) ?? fallback;

    const setRaw = (value: string | ((previous: string) => string)): void => {
      writeItem(
        area,
        key,
        typeof value === 'function' ? value(getRaw()) : value,
      );
    };

    return { get: getRaw, set: setRaw, remove };
  }

  const { fallback, parse = (parsed) => parsed as T } = options;

  const get = (): T => {
    const raw = readItem(area, key);
    if (raw === null) {
      return fallback;
    }
    try {
      return parse(JSON.parse(raw));
    } catch {
      return fallback;
    }
  };

  const set = (value: T | ((previous: T) => T)): void => {
    const next =
      typeof value === 'function'
        ? (value as (previous: T) => T)(get())
        : value;

    let serialized: string | undefined;
    try {
      serialized = JSON.stringify(next);
    } catch {
      return;
    }

    if (serialized === undefined) {
      remove();
      return;
    }
    writeItem(area, key, serialized);
  };

  return { get, set, remove };
}
